import { Fragment } from 'react';
import type { KioskBeverage } from './types';
import { formatEuroCents } from '../lib/format';

interface Props {
  beverages: KioskBeverage[];
  disabled: boolean;
  /**
   * 2026-09-26: Ist am Entnahme-Kiosk eine KISTENZAHL gewählt, sind Artikel ohne Gebinde nicht
   * antippbar -- Maß Goiß kommt vom Fass, eine Kiste davon gibt es nicht.
   *
   * Eine gesperrte Kachel ist besser als eine Meldung nach dem Tippen: Sie sagt es, bevor jemand
   * danach greift, statt hinterher zu erklären, warum nichts passiert ist.
   */
  requireCrateSize?: boolean;
  onSelect: (beverage: KioskBeverage) => void;
  /** v0.11 FR-195: which of the item's two prices to display -- the write path always re-resolves this server-side, this is display only. */
  showGuestPrice: boolean;
}

function Tile({
  beverage,
  disabled,
  onSelect,
  showGuestPrice,
}: {
  beverage: KioskBeverage;
  disabled: boolean;
  onSelect: (beverage: KioskBeverage) => void;
  showGuestPrice: boolean;
}) {
  // 2026-09-25 (Owner, nach dem Sammelgast-Event): Speisen tragen Kachel- und Schriftfarbe
  // VERTAUSCHT -- schwarz mit gelber Schrift statt umgekehrt. Kein neuer Farbton, nur die
  // Vereinsfarben andersherum. Die Überschriften bleiben trotzdem stehen: Farbe ist eine
  // Konvention, die jemand erst lernen muss, eine Überschrift sagt es sofort.
  const food = beverage.kind === 'SPEISE';
  /**
   * 2026-09-28 (Owner): Alkoholfreie Getränke blasser als alkoholische -- dritte Helligkeit
   * derselben Vereinsfarbe, kein dritter Farbton (Begründung an `.beverage-tile--nonalc`).
   *
   * **Nur bei GETRÄNKEN.** Eine Speise ist schwarz, und eine blasse schwarze Kachel gibt es
   * nicht; die Kategorie ist dort ohnehin ohne Bedeutung und wird auf der Artikelseite seit v0.11
   * gar nicht mehr abgefragt.
   *
   * Fehlt die Kategorie, bleibt die Kachel wie bisher voll gelb. Das ist die richtige Richtung:
   * In Prod trägt heute zwar jedes Getränk eine, aber ein neu angelegtes ohne Angabe sähe sonst
   * aus wie „alkoholfrei" -- die Aussage, die man nicht raten darf.
   */
  const alkoholfrei = !food && beverage.category === 'NONALC';
  return (
    <button
      className={`kiosk-tile beverage-tile${food ? ' beverage-tile--food' : ''}${
        alkoholfrei ? ' beverage-tile--nonalc' : ''
      }`}
      disabled={disabled}
      onClick={() => onSelect(beverage)}
    >
      <span>{beverage.name}</span>
      <span className="subtitle">
        {formatEuroCents(showGuestPrice ? beverage.guestPriceCents : beverage.memberPriceCents)}
      </span>
    </button>
  );
}

/**
 * FR-20/FR-64: one tap books one booking, at whatever quantity the parent's
 * stepper currently holds. v0.11 FR-192: Getränke and Speisen are visually
 * separated into their own sections rather than interleaved.
 *
 * 2026-09-25 (Owner): Die sichtbaren Überschriften „Getränke"/„Speisen" sind weg -- seit die
 * Speisen schwarz mit gelber Schrift sind, trennt die Farbe die beiden Gruppen deutlich genug,
 * und auf dem Tablet zählt jede Zeile.
 *
 * **Sie sind nur OPTISCH weg, nicht semantisch.** Jede Gruppe bleibt ein benannter Abschnitt
 * (`<section aria-label>`), sonst wäre aus einer sichtbaren Gliederung ein undifferenzierter
 * Haufen von 30 Knöpfen geworden -- für eine Vorlesehilfe ist eine Farbe kein Unterschied.
 * Kostet kein Pixel.
 */
export default function BeverageGrid({
  beverages,
  disabled,
  onSelect,
  showGuestPrice,
  requireCrateSize,
}: Props) {
  /**
   * 2026-09-27 (Owner, Wunsch der Bediener): DREI Gruppen statt zwei -- alkoholisch,
   * alkoholfrei, Speisen. Die Farben allein trennen zwar sichtbar, aber sie ordnen nicht: Am
   * Heimspiel stehen 13 alkoholische zwischen 11 alkoholfreien, und wer ein Wasser sucht, sucht
   * es überall.
   *
   * **Reihenfolge nach Helligkeit, nicht alphabetisch:** volles Gelb, blass, schwarz. So läuft
   * das Raster von kräftig nach dunkel durch, statt zweimal die Richtung zu wechseln.
   *
   * Ein Getränk OHNE Kategorie landet bei den alkoholfreien -- dieselbe Richtung wie bei der
   * Farbe, wo es voll gelb bleibt? NEIN, und das ist Absicht: Bei der FARBE ist „voll gelb" die
   * vorsichtige Wahl (ein fälschlich blasses Getränk sähe harmlos aus), bei der EINORDNUNG wäre
   * „zu den alkoholischen" die vorsichtige. Deshalb steht es hier bei den alkoholischen.
   */
  const drinks = beverages.filter((b) => b.kind !== 'SPEISE');
  const alkoholisch = drinks.filter((b) => b.category !== 'NONALC');
  const alkoholfrei = drinks.filter((b) => b.category === 'NONALC');
  const food = beverages.filter((b) => b.kind === 'SPEISE');
  const kacheln = (list: KioskBeverage[]) => (
    <div className="kiosk-grid">
      {list.map((b) => (
        <Tile
          key={b.beverageId}
          beverage={b}
          disabled={disabled || (requireCrateSize === true && !b.unitsPerCrate)}
          onSelect={onSelect}
          showGuestPrice={showGuestPrice}
        />
      ))}
    </div>
  );
  // Eine leere Gruppe erscheint gar nicht -- sonst stünde am Getränke-Kiosk einer Abteilung
  // ohne Speisen ein Abstand ohne Inhalt, und am Entnahme-Event gleich zwei.
  const gruppe = (label: string, liste: KioskBeverage[], erste: boolean) =>
    liste.length === 0 ? null : (
      <section aria-label={label} className={erste ? undefined : 'kiosk-grid-section'}>
        {kacheln(liste)}
      </section>
    );

  const gruppen = [
    ['Alkoholische Getränke', alkoholisch],
    ['Alkoholfreie Getränke', alkoholfrei],
    ['Speisen', food],
  ] as const;
  const ersteMitInhalt = gruppen.find(([, liste]) => liste.length > 0)?.[0];

  return (
    <div>
      {gruppen.map(([label, liste]) => (
        <Fragment key={label}>{gruppe(label, liste, label === ersteMitInhalt)}</Fragment>
      ))}
    </div>
  );
}
